import type { ActionTree, MutationTree, GetterTree } from 'vuex'
import RootState from '@web/store/types'
import type { Comment, CommentsState } from '@club/types/Comment'
import CommentAPI from '@club/api/modules/comment'
import type { Community } from '@club/types/Community'
import Logger from '@web/common/Logger'

const state: CommentsState = {
  list: {},
  count: {}
}

const commentAPI = new CommentAPI()

const getters: GetterTree<CommentsState, RootState> = {
  comments: ({ list }) => (articleId: string): Comment[] => list[articleId] || [],
  count: ({ count }) => (articleId: string): number => count[articleId] || 0
}

const mutations: MutationTree<CommentsState> = {
  addComment (state: CommentsState, payload: { article_id: string, comment: Comment }) {
    if (!state.list[payload.article_id]) {
      state.list[payload.article_id] = []
    }
    state.list[payload.article_id].unshift(payload.comment)
  },
  addComments (state: CommentsState, payload: { article_id: string, data: Comment[] }) {
    state.list[payload.article_id] = payload.data
  },
  setCount (state: CommentsState, payload: { article_id: string, count: number }) {
    state.count[payload.article_id] = payload.count
  },
  logout (state: CommentsState) {
    state.list = {}
    state.count = {}
  }
}

const actions: ActionTree<CommentsState, RootState> = {
  async fetch ({ commit, rootGetters }, payload: { article_id: number }) {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    let comments: Comment[] = []
    try {
      const data: Comment[] = await commentAPI.fetchComments(payload.article_id, {
        communityExternalIds: [selectedCommunity.uniq]
      })
      commit('addComments', {
        article_id: payload.article_id,
        data
      })
      comments = data
    } catch (e) {
      Logger.info('Error fetching comments', e)
    }

    return comments
  },

  async fetchAll ({ dispatch }, payload: { article_id: number }) {
    return dispatch('fetch', payload)
  },

  async sendMessage ({ commit, getters }, { article_id: articleId, ...payload }: { article_id: number, payload: Record<string, string> }) {
    let _comment: Comment | Record<string, never> = {}
    try {
      const comment: Comment = await commentAPI.sendMessage({ articleId, ...payload })
      commit('addComment', {
        article_id: articleId,
        comment
      })
      _comment = comment
      commit('setCount', {
        article_id: articleId,
        count: getters.count(articleId) + 1
      })
    } catch (e) {
      Logger.info('Error send comment', e)
    }

    return _comment
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
